import type { LoadingButtonProps } from '@mui/lab/LoadingButton';

import LoadingButton from '@mui/lab/LoadingButton';
import { useFormContext, useWatch } from 'react-hook-form';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import React from 'react';

import './FormSubmitButton.scss';

export interface SubmitButtonProps extends LoadingButtonProps {
  disableIfNotDirty?: boolean;
  deactivateDisable?: boolean;
  disabledTooltip?: string;
  /**
   * Also disable while the form is invalid (formState.isValid). Off by default because
   * many forms rely on server-side setError() to surface errors after submit, and gating
   * on isValid there would re-lock the button until an unrelated field is touched.
   */
  requireValid?: boolean;
}

export const FormSubmitButton = React.forwardRef(
  (
    {
      children,
      deactivateDisable,
      disableIfNotDirty = false,
      disabled,
      disabledTooltip,
      fullWidth,
      loading,
      requireValid = false,
      ...props
    }: SubmitButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const {
      control,
      formState: { defaultValues, isSubmitting, isValidating, isValid },
    } = useFormContext();

    const values = useWatch({ control });

    /**
     * If you encounter an issue where your form submit button is enabled and you would expect
     * it to be disabled because values in form and default values are the same, it might be that either one of those two
     * has an value which you set wrongly. In those cases, you can use `getChangedPaths` from @mfw/components to see which keys
     * are different between those two objects and you can quickly discover your issue.
     */
    const isEqualWithDefaults = (() => {
      if (disableIfNotDirty) return false;
      return isEqual(defaultValues, values);
    })();

    const isDisabled = deactivateDisable
      ? false
      : disabled ||
        isValidating ||
        isEqualWithDefaults ||
        (requireValid && !isValid);

    const isLoading = loading || isValidating || isSubmitting;
    return (
      <Tooltip title={disabledTooltip} open={isDisabled} placement="top">
        <LoadingButton
          disabled={isDisabled}
          loading={isLoading}
          variant="contained"
          disableRipple
          type="submit"
          size="large"
          ref={ref}
          {...props}
          className={classNames(
            'Mfw-FormSubmitButton-root',
            {
              'w-full': fullWidth,
            },
            props?.className,
          )}
        >
          {children}
        </LoadingButton>
      </Tooltip>
    );
  },
);

FormSubmitButton.displayName = 'SubmitButton';

export default FormSubmitButton;
